import React from 'react';
import { isEmpty, get } from 'lodash';

import { CSVLink } from 'react-csv';
import StatusOverviewChart from './statusOverviewChart';
import { COLOR_PALETTE, numberTickFormatter } from '../charts/chartUtils';
import { toCurrency, toLocale } from '../../../helpers/numberHelper';
import { mergeData, formatForExport } from './utils';
import dayjs from 'utils/dayjs';

import './statusOverview.style.css';

const statusOverview = ({ type, currency, canViewForecast, current, lyFinal, lySpit, forecast = null, budget, dayComparison = true }) => {
    if (!current || !lyFinal || !lySpit || !budget) return null;

    const mergedData = mergeData(current, lyFinal, lySpit, forecast, budget, dayComparison);
    const isRevenue = type === 'Revenue';
    const typeKey = isRevenue ? 'revenue.accommodation' : 'rooms';
    const scaleValue = (value) => {
        if (value === null) return null;
        return isRevenue ? value / 100 : value;
    };

    const bars = [
        {
            dataKey: (row) => scaleValue(get(row, `current.${typeKey}.guaranteed`)),
            dataName: 'Guaranteed',
            color: COLOR_PALETTE.cyan,
        },
        {
            dataKey: (row) => scaleValue(get(row, `current.${typeKey}.nonGuaranteed`)),
            dataName: 'Unguaranteed',
            color: COLOR_PALETTE.yellow,
        },
    ];
    const lines = [
        {
            dataKey: (row) => scaleValue(get(row, `lySpit.${typeKey}.guaranteed`)),
            dataName: 'Last year SPIT',
            color: COLOR_PALETTE.blue,
        },
        {
            dataKey: (row) => scaleValue(get(row, `lyFinal.${typeKey}.guaranteed`)),
            dataName: 'Last year Final',
            color: COLOR_PALETTE.purple,
        },
        {
            dataKey: (row) => scaleValue(get(row, `budget.${typeKey}`)),
            dataName: 'Budget',
            color: COLOR_PALETTE.seaGreen,
        },
    ];
    if (canViewForecast && !isEmpty(forecast)) {
        lines.push({
            dataKey: (row) => scaleValue(isRevenue ? get(row, 'forecast.revenue') : get(row, 'forecast.rooms')),
            dataName: 'Forecast',
            color: COLOR_PALETTE.black,
        });

        if (forecast.some((f) => f.unconstrained)) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < forecast.length; i++) {
                const f = forecast[i];
                if (!f.unconstrained) {
                    f.unconstrained = {
                        ...f,
                    };
                }
            }
            lines.push({
                dataKey: (row) =>
                    scaleValue(
                        isRevenue
                            ? get(row, 'forecast.unconstrained.revenue')
                            : get(row, 'forecast.unconstrained.rooms'),
                    ),
                dataName: 'Unconstrained Forecast',
                color: COLOR_PALETTE.black,
                isDotted: true,
            });
        }
    }

    const currencyTooltipValueFormatter = (value) => toCurrency(value, currency);
    const localeTooltipValueFormatter = (value) => toLocale(value);
    const priceTickFormatter = (value) => toCurrency(value, currency);

    const formatters = {
        // Just enough for up to £99,999 // 999 rooms
        yAxisWidth: isRevenue ? 45 : 25,
        yAxisTickFormatter: isRevenue ? priceTickFormatter : numberTickFormatter,
        tooltipFormatter: isRevenue ? currencyTooltipValueFormatter : localeTooltipValueFormatter,
    };
    const csvData = formatForExport(mergedData, [...bars, ...lines], formatters.yAxisTickFormatter);

    return (
        <>
            <div className="StatusOverview">
                <StatusOverviewChart
                    data={mergedData}
                    bars={bars}
                    lines={lines}
                    xAxisDataKey={(row) => dayjs(row.stayDate).format('ddd DD MMM')}
                    syncId="roomAndRevenue"
                    {...formatters}
                    allowDecimalsInYAxis={isRevenue}
                />
            </div>
            <div className="StatusOverviewFooter">
                <CSVLink className="StatusOverviewFooterLink" filename={`${type}StatusOverview.csv`} data={csvData}>
                    Export to CSV
                </CSVLink>
            </div>
        </>
    );
};

export default statusOverview;
